import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";

import { CellContext } from "@/lib/Components/DataTable/types";
import {
  BookingListQueryVariables,
  BookingRelocationMatchType,
  BookingStatus,
  QueryRelocationsWhereColumn,
  QueryRelocationsWhereVehicleColumn,
  RelocationStatus,
  RelocationType,
  SqlOperator,
  RelocationInclusionType,
} from "@/gql/graphql";
import {
  formatCurrency,
  formatDistance,
} from "@/lib/Formatters/formatCurrency";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useState } from "react";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingFindRelocationDialog } from "@/app/Bookings/Components/BookingFindRelocationDialog";
import { formatDateTime } from "@/lib/Formatters/formatDateTime";
import { useTranslations } from "use-intl";
import { Link } from "@/components/catalyst/link";
import { formatDate } from "@/lib/Formatters/formatDate";
import {
  BookingListItem,
  bookingListQuery,
} from "@/app/Bookings/GraphQL/bookingListQuery";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Badge } from "@/components/catalyst/badge";
import { bookingStatusColorMap } from "@/app/Bookings/Utils/bookingStatusColorMap";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { inclusionIconMap } from "@/app/Relocations/Utils/inclusionIconMap";
import {
  MultiSelectFilter,
  MultiSelectFilterOption,
} from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import {
  SelectFilter,
  SelectFilterOption,
} from "@/lib/Components/DataTable/Filters/SelectFilter";
import type { TableId } from "@/app/Common/Utils/table-id";

import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { useBookingActions } from "@/app/Bookings/Hooks/useBookingActions";
import { BookingGraphic } from "@/app/Bookings/Components/BookingGraphic";
import { CityFilter } from "@/lib/Components/DataTable/Filters/CityFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import { VehicleFilter } from "@/lib/Components/DataTable/Filters/VehicleFilter";
import {
  CopyToClipboard,
  CopyToClipboardButton,
} from "@/lib/Components/Common/CopyToClipboard";
import { Highlighter } from "@/lib/Components/Highlighter";
import { ArrowRightIcon } from "lucide-react";
import {
  bookingTableDefaultSorting,
  getBookingTableVariables,
} from "@/app/Dashboard/Utils/getBookingTableVariables";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { relocationMatchListQuery } from "@/app/Relocations/GraphQL/relocationListQuery";
import { useInView } from "react-intersection-observer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { bookingCancelledByQuery } from "@/app/Bookings/GraphQL/bookingCancelledByQuery";
import {
  BookingTableState,
  defaultBookingTableState,
} from "@/app/Bookings/Utils/bookingTableState";

type BookingStatusType =
  | "pending"
  | "vip"
  | "confirmed"
  | "completed"
  | "cancelled"
  | "incomplete";

function getStatusOptions(
  t: (key: string) => string,
): MultiSelectFilterOption<BookingStatusType>[] {
  return [
    {
      label: t("pending"),
      value: "pending",
    },
    {
      label: t("vip"),
      value: "vip",
    },
    {
      label: t("confirmed"),
      value: "confirmed",
    },
    {
      label: t("completed"),
      value: "completed",
    },
    {
      label: t("cancelled"),
      value: "cancelled",
    },
    {
      label: t("abandonedCheckout"),
      value: "incomplete",
    },
  ];
}

const statusTypeToStatusMap: Record<BookingStatusType, BookingStatus[]> = {
  pending: [BookingStatus.AwaitingConfirmation, BookingStatus.Accepted],
  vip: [BookingStatus.Vip],
  confirmed: [BookingStatus.Confirmed],
  completed: [BookingStatus.Completed],
  cancelled: [
    BookingStatus.AdminCancelled,
    BookingStatus.SupplierCancelledNotAvailable,
    BookingStatus.SupplierCancelledNoFerry,
    BookingStatus.SupplierCancelledMechanicalFailure,
    BookingStatus.SupplierCancelled,
    BookingStatus.CustomerCancelled,
    BookingStatus.CustomerCancelledNoShow,
    BookingStatus.CustomerCancelledRebooked,
    BookingStatus.CustomerCancelledConfirmationTimeliness,
  ],
  incomplete: [BookingStatus.AwaitingPayment],
};

function getRelocationTypeOptions(
  t: (key: string) => string,
): MultiSelectFilterOption<RelocationType>[] {
  return [
    {
      label: t(`typeLabels.${RelocationType.Relocation}`),
      value: RelocationType.Relocation,
    },
    {
      label: t(`typeLabels.${RelocationType.GapRental}`),
      value: RelocationType.GapRental,
    },
  ];
}

function getMatchTypeOptions(
  t: (key: string) => string,
): SelectFilterOption<BookingRelocationMatchType>[] {
  return [
    {
      label: t("exact"),
      value: BookingRelocationMatchType.Exact,
    },
    {
      label: t("similar"),
      value: BookingRelocationMatchType.Similar,
    },
    {
      label: t("broad"),
      value: BookingRelocationMatchType.Broad,
    },
  ];
}

function getStatusTypes(statuses: BookingStatus[]): BookingStatusType[] {
  const foundTypes: BookingStatusType[] = [];
  for (const [key, value] of Object.entries(statusTypeToStatusMap)) {
    //Test that all statuses are present
    const areAllStatusesPresent = value.every((status) =>
      statuses.includes(status),
    );
    if (areAllStatusesPresent) {
      foundTypes.push(key as BookingStatusType);
    }
  }

  return foundTypes;
}

type BookingTableProps = {
  queryVariables?: Partial<BookingListQueryVariables>;
  title?: string;
  hiddenColumns?: BookingColumnId[];
  id: TableId;

  //Un-controlled component
  defaultState?: Partial<BookingTableState>;

  //Controlled component
  state?: BookingTableState;
  onStateChange?: (state: BookingTableState) => void;

  searchable?: boolean;
  supplierId?: string;
};

export type BookingColumnId =
  | "booking"
  | "status"
  | "matches"
  | "relocation"
  | "dates"
  | "customer"
  | "pax"
  | "trip"
  | "actions"
  | "created_at"
  | "journey";

export function BookingTable({
  title,
  id,
  hiddenColumns,

  //Controlled
  state: stateProp,
  onStateChange,

  //Un-controlled
  defaultState,

  searchable,

  queryVariables,
  supplierId: supplierIdProp,
}: BookingTableProps) {
  "use no memo";

  const t = useTranslations("booking");
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const scopedSupplierId = supplierIdProp ?? globalSupplierId;
  const getActions = useBookingActions();

  const [localState, setLocalState] = useState<BookingTableState>({
    ...defaultBookingTableState,
    ...defaultState,
  });

  const state = stateProp ?? localState;

  // Any change other than paging puts the user back on the first page.
  const updateState = (patch: Partial<BookingTableState>) => {
    const next = { ...state, page: 1, ...patch };

    if (onStateChange) {
      onStateChange(next);
    } else {
      setLocalState(next);
    }
  };

  const {
    statuses: status,
    departureCityIds,
    deliveryCityIds,
    vehicles,
    relocationTypes,
    hasMatches,
  } = state;
  const supplierId = scopedSupplierId ?? state.supplierId;

  const { isAdmin } = useTenant();

  const columns: DataTableColDef<BookingListItem, unknown, BookingColumnId>[] =
    [
      {
        id: "booking",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("booking")} />
        ),
        accessorFn: (row) => row,
        cell: BookingCell,
      },
      {
        id: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("statusLabel")} />
        ),
        accessorFn: (row) => row,
        cell: StatusCell,
      },

      {
        id: "journey",
        accessorFn: (row) => row,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("journey")} />
        ),
        size: 280,
        cell: ({ row }) => {
          return <JourneyCell booking={row.original} />;
        },
      },
      {
        id: "customer",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("driver")} />
        ),
        accessorFn: (row) => row,
        cell: CustomerCell,
        size: 220,
      },
      {
        id: "trip",
        header: t("deal"),
        cell: TripCell,
      },
      {
        id: "created_at",
        accessorFn: (row) => row,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("created")} />
        ),
        cell: ({ row }) => (
          <TimeSinceNow
            className="text-xs text-gray-500"
            time={row.original.created_at}
          />
        ),
      },
    ];

  return (
    <DataTable
      id={id}
      title={title}
      page={state.page}
      onPageChange={(page) => updateState({ page })}
      search={state.search}
      onSearchChange={(search) => updateState({ search })}
      hiddenColumns={hiddenColumns}
      getActions={getActions}
      initialSorting={bookingTableDefaultSorting}
      document={bookingListQuery}
      columns={columns}
      emptyState={<BookingEmptyState />}
      accessor={(data) => data.bookings}
      searchable={searchable}
      searchPlaceholder={t("searchPlaceholder")}
      filters={
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label={t("statusFilter")}
            options={getStatusOptions(t).filter((option) => {
              return option.value !== "vip" || isAdmin;
            })}
            selected={getStatusTypes(status)}
            onChange={(values) => {
              updateState({
                statuses: values.flatMap(
                  (value) => statusTypeToStatusMap[value],
                ),
              });
            }}
          />
          {scopedSupplierId ? null : (
            <SupplierFilter
              label={t("supplierFilter")}
              selected={state.supplierId}
              onChange={(supplierId) => updateState({ supplierId })}
            />
          )}
          <CityFilter
            placeholder={t("departingFrom")}
            selected={departureCityIds}
            onChange={(departureCityIds) => updateState({ departureCityIds })}
          />
          <CityFilter
            placeholder={t("deliveringTo")}
            selected={deliveryCityIds}
            onChange={(deliveryCityIds) => updateState({ deliveryCityIds })}
          />
          <VehicleFilter
            placeholder={t("vehicle")}
            selected={vehicles}
            onChange={(vehicles) => updateState({ vehicles })}
          />
          <MultiSelectFilter
            label={t("typeFilter")}
            options={getRelocationTypeOptions(t)}
            selected={relocationTypes}
            onChange={(relocationTypes) => updateState({ relocationTypes })}
          />
          {isAdmin ? (
            <SelectFilter
              label={t("hasMatches")}
              options={getMatchTypeOptions(t)}
              selected={hasMatches}
              onChange={(hasMatches) => updateState({ hasMatches })}
            />
          ) : null}
        </div>
      }
      getQueryVariables={getBookingTableVariables({
        supplierId,
        status,
        departureCityIds,
        deliveryCityIds,
        vehicles,
        relocationTypes,
        hasMatches,
        queryVariables,
      })}
    />
  );
}

function getInclusionIconColor(type: RelocationInclusionType): string {
  switch (type) {
    case RelocationInclusionType.Fuel:
      return "text-green-600";
    case RelocationInclusionType.Ferry:
      return "text-blue-600";
    default:
      return "text-yellow-500";
  }
}

function JourneyCell({ booking }: { booking: BookingListItem }) {
  const relocation = booking.relocation;

  // Calculate time until departure
  const diffInHours = dayjs(booking.depart_at).diff(dayjs(), "hours");
  let diffText = "<72hrs";
  let isUrgent = false;

  if (diffInHours < 0) {
    diffText = "expired";
    isUrgent = true;
  } else if (diffInHours === 0) {
    diffText = "now";
    isUrgent = true;
  } else if (diffInHours <= 24) {
    diffText = "<24hrs";
    isUrgent = true;
  } else if (diffInHours <= 48) {
    diffText = "<48hrs";
    isUrgent = true;
  }

  // Only show badge for AwaitingConfirmation and Vip statuses
  const showUrgencyBadge =
    diffInHours <= 72 &&
    [BookingStatus.AwaitingConfirmation, BookingStatus.Vip].includes(
      booking.status,
    );

  return (
    <div className="flex flex-col space-y-2 text-xs">
      {/* Relocation Header with Info Button */}
      <p className="line-clamp-1 flex items-center space-x-1 truncate">
        {booking.relocation.type === RelocationType.GapRental ? (
          <>
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
              Gap rental
            </span>
            <span className="">{booking.departureOffice.name}</span>
          </>
        ) : (
          <>
            <span className="">{booking.departureOffice.name}</span>
            <ArrowRightIcon className="size-3.5 flex-shrink-0" />
            <span className="">{booking.deliveryOffice.name}</span>
          </>
        )}
        <span className="">({booking.relocation.reference})</span>
        <a
          href={booking.relocation.customer_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ArrowTopRightOnSquareIcon className="size-3.5" />
        </a>
      </p>

      {/* Vehicle and Supplier Info */}
      <div className="flex items-center text-gray-500">
        <span className="truncate">
          {booking.vehicle.name} ({booking.supplier.name})
        </span>
      </div>

      {/* Dates with Icons */}
      <div className="flex flex-col gap-1 text-gray-500">
        <div className="flex items-start">
          <div className="flex flex-wrap items-center gap-x-1">
            <CopyToClipboard
              title={dayjs(booking.depart_at).format("LLLL")}
              text={formatDateTime(booking.depart_at)}
              className="text-gray-700"
            />
            {showUrgencyBadge && (
              <Badge
                color={isUrgent ? "red" : "amber"}
                className="ml-1 px-1 py-0"
              >
                {diffText}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center gap-x-1">
            <CopyToClipboard
              title={dayjs(booking.deliver_at).format("LLLL")}
              text={formatDateTime(booking.deliver_at)}
              className="text-gray-700"
            />
          </div>
        </div>
      </div>

      {/* Inclusions Section */}
      {booking.inclusions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {booking.inclusions.map(
            ({ id, type, value, description, has_free_tank }) => {
              const Icon = inclusionIconMap[type];

              // Build tooltip content
              let tooltipContent = "";
              if (has_free_tank) {
                tooltipContent = "One Free Tank";
              } else {
                if (value) {
                  tooltipContent = formatCurrency(value, relocation.currency);
                }
                if (description) {
                  tooltipContent +=
                    (tooltipContent ? " - " : "") +
                    description.replace(/<[^>]*>/g, "");
                }
              }

              return (
                <Tooltip key={id}>
                  <TooltipTrigger asChild>
                    <div className="cursor-help">
                      <Icon
                        className={cn("h-4 w-4", getInclusionIconColor(type))}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{tooltipContent || "Inclusion"}</p>
                  </TooltipContent>
                </Tooltip>
              );
            },
          )}
        </div>
      )}
    </div>
  );
}

function Matches({ booking }: { booking: BookingListItem }) {
  const { ref, inView } = useInView({
    triggerOnce: true,
  });
  const { open } = useDialog(BookingFindRelocationDialog);
  const { data } = useGqlQuery(
    relocationMatchListQuery,
    {
      first: 1,
      page: 1,
      status: [RelocationStatus.Ready],
      departureCityId: booking.departureOffice.city_id,
      deliveryCityId: booking.deliveryOffice.city_id,
      where: {
        AND: [
          {
            column: QueryRelocationsWhereColumn.Id,
            value: booking.relocation.id,
            operator: SqlOperator.Neq,
          },
          {
            column: QueryRelocationsWhereColumn.AvailableFromDate,
            value: dayjs(booking.depart_at).format("YYYY-MM-DD"),
            operator: SqlOperator.Lte,
          },
          {
            column: QueryRelocationsWhereColumn.LatestDepartureDate,
            value: dayjs(booking.depart_at).format("YYYY-MM-DD"),
            operator: SqlOperator.Gte,
          },
          {
            column: QueryRelocationsWhereColumn.AvailableToDate,
            value: dayjs(booking.deliver_at).format("YYYY-MM-DD"),
            operator: SqlOperator.Gte,
          },
        ],
      },
      whereVehicle: {
        AND: [
          {
            column: QueryRelocationsWhereVehicleColumn.Type,
            value: booking.vehicle.type,
            operator: SqlOperator.Eq,
          },
          {
            column: QueryRelocationsWhereVehicleColumn.Seatbelts,
            value: booking.number_of_travellers,
            operator: SqlOperator.Gte,
          },
          {
            column: QueryRelocationsWhereVehicleColumn.MinimumAge,
            operator: SqlOperator.Lte,
            value: dayjs(booking.depart_at).diff(booking.date_of_birth, "year"),
          },
        ],
      },
    },
    {
      enabled: inView,
    },
  );

  return (
    <span ref={ref} className="text-xs">
      <button
        className={cn("text-gray-500 hover:underline", {
          "text-emerald-600": Boolean(data?.relocations.paginatorInfo.total),
        })}
        title="matches"
        onClick={(e) => {
          e.stopPropagation();
          open({
            bookingId: booking.id,
          });
        }}
      >
        {data?.relocations.paginatorInfo.total ?? 0} matches
      </button>
    </span>
  );
}

function BookingCell({ row, table }: CellContext<BookingListItem, any>) {
  "use no memo";
  const booking = row.original;
  const { isAdmin } = useTenant();

  const hasLineExpired = booking.relocationLine?.group.expires_at
    ? dayjs(booking.relocationLine?.group.expires_at, {
        utc: true,
      }).isBefore(dayjs())
    : false;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center space-x-1">
        <Link
          to={"/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"}
          params={(prev) => ({
            ...prev,
            bookingId: booking.id,
          })}
          viewTransition
          className="font-medium text-blue-500 underline"
        >
          <Highlighter searchString={table.options.meta?.search ?? ""}>
            {booking.reference}
          </Highlighter>
        </Link>

        <CopyToClipboardButton text={booking.reference} />
      </div>
      {booking.relocationLine ? (
        <div
          className={cn("flex max-w-40 items-center", {
            "text-red-500": hasLineExpired,
          })}
        >
          <span
            className="truncate font-medium"
            title={booking.relocationLine.reference}
          >
            {booking.relocationLine?.reference}
          </span>
          {hasLineExpired ? (
            <span className="ml-1 text-xs font-normal"> (expired)</span>
          ) : null}
        </div>
      ) : null}

      <p>{isAdmin ? <Matches booking={booking} /> : null}</p>
    </div>
  );
}

function StatusCell({ row, table }: CellContext<BookingListItem, any>) {
  const booking = row.original;
  const t = useTranslations("booking");

  const isCancelled =
    booking.status === BookingStatus.AdminCancelled ||
    booking.status === BookingStatus.CustomerCancelled ||
    booking.status === BookingStatus.CustomerCancelledConfirmationTimeliness ||
    booking.status === BookingStatus.CustomerCancelledNoShow ||
    booking.status === BookingStatus.CustomerCancelledRebooked ||
    booking.status === BookingStatus.SupplierCancelled ||
    booking.status === BookingStatus.SupplierCancelledMechanicalFailure ||
    booking.status === BookingStatus.SupplierCancelledNotAvailable ||
    booking.status === BookingStatus.SupplierCancelledNoFerry;

  return (
    <div className="flex flex-col gap-1">
      <div>
        {isCancelled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                color={bookingStatusColorMap[booking.status]}
                className="inline-block cursor-help"
              >
                <p className="capitalize">
                  {t(`statusShortLabel.${booking.status}`)}
                </p>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <CancelledByTooltip booking={booking} />
            </TooltipContent>
          </Tooltip>
        ) : (
          <Badge
            color={bookingStatusColorMap[booking.status]}
            className="inline-block"
          >
            <p className="capitalize">
              {t(`statusShortLabel.${booking.status}`)}
            </p>
          </Badge>
        )}
      </div>

      {booking.supplier_reference ? (
        <Highlighter
          searchString={table.options.meta?.search ?? ""}
          className="max-w-40 overflow-hidden overflow-ellipsis whitespace-nowrap"
        >
          {booking.supplier_reference}
        </Highlighter>
      ) : null}
    </div>
  );
}

function CancelledByTooltip({ booking }: { booking: BookingListItem }) {
  const t = useTranslations("booking");
  const { data } = useGqlQuery(bookingCancelledByQuery, {
    id: booking.id,
  });

  return (
    <>
      <p className="capitalize">{t(`status.${booking.status}`)}</p>
      <p>By {data?.booking.cancelledByActivity?.causer?.name ?? "--"}</p>
      <p>
        At{" "}
        {data?.booking.cancelledByActivity?.created_at
          ? formatDateTime(data.booking.cancelledByActivity.created_at)
          : "--"}
      </p>
    </>
  );
}

function CustomerCell({ row, table }: CellContext<BookingListItem, any>) {
  "use no memo";
  const booking = row.original;
  const search = table.options.meta?.search ?? "";

  return (
    <div className="flex flex-col space-y-1.5 text-xs">
      <div className="flex items-center">
        <CopyToClipboard text={booking.name}>
          <Highlighter searchString={search}>{booking.name}</Highlighter>
        </CopyToClipboard>
        <span className="ml-2 text-gray-500">
          ({booking.number_of_travellers} pax)
        </span>
      </div>

      <div className="space-y-1 text-gray-500">
        <CopyToClipboard text={booking.email}>
          <Highlighter searchString={search} className="max-w-full truncate">
            {booking.email}
          </Highlighter>
        </CopyToClipboard>

        <CopyToClipboard text={booking.phone}>
          <Highlighter searchString={search}>{booking.phone}</Highlighter>
        </CopyToClipboard>

        <CopyToClipboard text={formatDate(booking.date_of_birth)} />
      </div>
    </div>
  );
}

function TripCell({ row }: CellContext<BookingListItem, any>) {
  const booking = row.original;

  const t = useTranslations("relocation");
  const hireUnitLabel = t(
    `hire_unit_type.${booking.relocation.hire_unit_type}`,
  );

  // Gap rentals have no discounted relocation units, so "0 + 3 nights"
  // collapses to "3 nights".
  let unitLabel = `${booking.discounted_units}`;
  if (booking.relocation.type === RelocationType.GapRental) {
    unitLabel = `${booking.full_price_units}`;
  } else if (booking.full_price_units > 0) {
    unitLabel += ` + ${booking.full_price_units}`;
  }
  unitLabel += ` ${hireUnitLabel}s`;

  return (
    <div className="flex flex-col gap-1 text-xs text-gray-500">
      <div className="flex shrink-0 items-center whitespace-nowrap">
        <span>{unitLabel}</span>
      </div>

      <div className="flex shrink-0 items-center">
        <span>
          {formatDistance(
            booking.distance_allowed,
            booking.relocation.measurement,
          )}
        </span>
      </div>
    </div>
  );
}

function BookingEmptyState() {
  const t = useTranslations("booking.bookingEmpty");

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-6 text-center">
      <div className="w-full max-w-sm">
        <BookingGraphic />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">{t("title")}</p>
        <p className="mt-1 text-xs text-gray-500">{t("subtitle")}</p>
      </div>
    </div>
  );
}
